import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  Typography,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import DailyPracticeCard from "./DailyPracticeCard";
import { useLocation } from "react-router-dom";
import { getAllDailyPractice, getDailyPracticeQR } from "../../../services/mentorServices/DailyPracticeServices";
import { toast } from "react-toastify";
import Footer from "./Footer";

const DailyPracticeDashboard = () => {
  const location = useLocation();
  const { coeId, labId, labs } = location.state || {};
  const [dailyPractices, setDailyPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [dialogMode, setDialogMode] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [selectedLab, setSelectedLab] = useState(labId);

  useEffect(() => {
    if (coeId && selectedLab) {
      setLoading(true);
      getAllDailyPractice(coeId, selectedLab)
        .then((response) => {
          setDailyPractices(response.data.data);
          setError(null);
        })
        .catch((err) => {
          const errorMsg = err?.response?.data?.message || "Failed to fetch daily practices.";
          setError(errorMsg);
          toast.error(errorMsg);
        })
        .finally(() => setLoading(false));
    }
  }, [coeId, selectedLab]);

  const fetchQR = useCallback(async () => {
    if (open && dialogMode === "qr" && selectedPractice) {
      setQrLoading(true);
      try {
        const response = await getDailyPracticeQR(selectedPractice.daily_practice_id);
        const blob = new Blob([response.data], { type: "image/png" });
        setQrCode(URL.createObjectURL(blob));
      } catch (error) {
        const errorMsg = error?.response?.data?.message || error?.message;
        toast.error(errorMsg);
        setQrCode(null);
      } finally {
        setQrLoading(false);
      }
    }
  }, [open, dialogMode, selectedPractice]);

  useEffect(() => {
    fetchQR();
  }, [fetchQR]);

  const labOptions = useMemo(() => {
    return labs?.map((lab) => (
      <MenuItem key={lab?.id} value={lab?.id}>
        {lab?.lab_name}
      </MenuItem>
    ));
  }, [labs]);

  const handleClickOpen = (practice, mode) => {
    setSelectedPractice(practice);
    setDialogMode(mode);
    setOpen(true);
    if (mode === "qr") {
      setQrCode(null);
      setQrLoading(true);
    }
  };

  const handleClose = () => {
    setQrCode(null);
    setOpen(false);
    setDialogMode(null);
    setSelectedPractice(null);
    setQrLoading(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, maxWidth: 400, mx: "auto", bgcolor: "background.paper" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0, maxWidth: 400, mx: "auto", bgcolor: "background.paper", pb: 10 }}>
      <FormControl size="small" sx={{ width: "100% !important", mb: 2 }} error={!!error?.lab}>
        <InputLabel id="lab-select-label">Lab Name*</InputLabel>
        <Select
          labelId="lab-select-label"
          id="lab-select"
          value={selectedLab}
          onChange={(event) => setSelectedLab(event.target.value)}
          label="Lab Name"
        >
          {labOptions}
        </Select>
        {error?.lab && <FormHelperText>{error?.lab}</FormHelperText>}
      </FormControl>
      <Typography variant="h6" align="center" fontWeight="bold" mb={2}>
        Daily Practice
      </Typography>

      {dailyPractices.length > 0 ? (
        dailyPractices.map((practice) => (
          <Card key={practice.daily_practice_id} sx={{ p: 2, mb: 2, boxShadow: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{practice.daily_practice_name}</Typography>
              <Box display="flex" gap={1}>
                <Button variant="contained" onClick={() => handleClickOpen(practice, "qr")}>QR</Button>
                <Button variant="contained" onClick={() => handleClickOpen(practice, "view")}>View</Button>
              </Box>
            </Box>

            <Box display="flex" flexDirection="column" gap={1} mt={2}>
              <Box display="flex" justifyContent="space-between">
                <Typography>Topic:</Typography>
                <Typography>{practice.Topic?.topic_Name}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Type:</Typography>
                <Typography>{practice.coding_type}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Coding Speed:</Typography>
                <Typography>{practice.coding_speed}</Typography>
              </Box>
            </Box>
          </Card>
        ))
      ) : (
        <Typography>No daily practices found for this COE and lab.</Typography>
      )}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogContent sx={{ p: 0, width: "100%" }}>
          {dialogMode === "view" && selectedPractice && (
            <DailyPracticeCard selectedPractice={selectedPractice} sx={{ width: "100%" }} />
          )}
          {dialogMode === "qr" && (
            qrLoading ? (
              <CircularProgress />
            ) : qrCode ? (
              <img src={qrCode} alt="QR Code" style={{ width: "100%" }} />
            ) : (
              <Typography>No QR code available</Typography>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      <Footer coeId={coeId} labId={labId} labs={labs} sx={{ mt: 10 }} />
    </Box>
  );
};

export default DailyPracticeDashboard;
